import { FC, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import CommonLoader from '@/components/CommonLoader';
import { useAuth } from '@/contexts/AuthContext';
import { usePermission } from '@/contexts/PermissionContext';
import { DASHBOARD_URL } from '@/utils/constants/paths';
import { matchesPermissionPattern } from '@/utils/constants/report-permissions';
import { makerRequestPermission } from '@/utils/maker-checker-utils';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: 'admin' | 'tenant';
}

export const ProtectedRoute: FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
}) => {
  const { isAuthenticated, role, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <CommonLoader isLoading={true} />;
  }

  if (!isAuthenticated) {
    return <Navigate to='/' state={{ from: location }} replace />;
  }

  if (requiredRole && role !== requiredRole) {
    return <Navigate to={DASHBOARD_URL} replace />;
  }

  return <>{children}</>;
};

interface PublicRouteProps {
  children: ReactNode;
}

export const PublicRoute: FC<PublicRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <CommonLoader isLoading={true} />;
  }

  if (isAuthenticated) {
    return <Navigate to={DASHBOARD_URL} replace />;
  }

  return <>{children}</>;
};

export const Authenticated: FC<{ children: ReactNode }> = ({ children }) => {
  return <ProtectedRoute>{children}</ProtectedRoute>;
};

export const AuthRedirect: FC<{ children: ReactNode }> = ({ children }) => {
  return <PublicRoute>{children}</PublicRoute>;
};

interface PermissionProtectedRouteProps {
  children: ReactNode;
  /** Any one of these codes opens the route. */
  requiredPermissions?: string[];
  /**
   * Alternatively (or additionally), a regex matched against the user's
   * permission codes — for routes whose codes are dynamic, e.g. the
   * per-report `Report.<code>.Run` codes.
   */
  requiredPermissionPattern?: string;
}

export const PermissionProtectedRoute: FC<PermissionProtectedRouteProps> = ({
  children,
  requiredPermissions = [],
  requiredPermissionPattern,
}) => {
  const { hasAnyPermission, permissions } = usePermission();
  const { isLoading } = useAuth();

  if (isLoading) {
    return <CommonLoader isLoading={true} />;
  }

  const allowedByList =
    requiredPermissions.length > 0 && hasAnyPermission(requiredPermissions);
  const allowedByPattern =
    Boolean(requiredPermissionPattern) &&
    matchesPermissionPattern(
      (permissions ?? []).map(permission => permission.code),
      requiredPermissionPattern as string
    );

  if (!allowedByList && !allowedByPattern) {
    return <Navigate to={DASHBOARD_URL} replace />;
  }

  return <>{children}</>;
};

interface MakerRequestRouteProps {
  children: ReactNode;
  /** The module's permission prefix, e.g. `Award`. */
  permissionPrefix: string;
}

/**
 * Guards a maker's approve-flow (revision) route: `<prefix>.Edit` opens it, or
 * `<prefix>.Delete` for a deletion request. An in-app click says which with
 * `isDelete` in the route state; a pasted link or new tab carries no state, so
 * either permission opens it and the page enforces what the user may do.
 */
export const MakerRequestRoute: FC<MakerRequestRouteProps> = ({
  children,
  permissionPrefix,
}) => {
  const location = useLocation();
  const isDelete = (location.state as { isDelete?: boolean } | null)?.isDelete;
  const requiredPermissions =
    typeof isDelete === 'boolean'
      ? [makerRequestPermission(permissionPrefix, isDelete)]
      : [
          makerRequestPermission(permissionPrefix, false),
          makerRequestPermission(permissionPrefix, true),
        ];

  return (
    <PermissionProtectedRoute requiredPermissions={requiredPermissions}>
      {children}
    </PermissionProtectedRoute>
  );
};
